import { doesAttachmentStrategyUseCustomFee } from "@redotech/redo-storefront-common/attachment-strategy";
import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import {
  ExtensionShopifyClient,
  ShopifyAjaxClient,
} from "@redotech/shopify-client/ajax";
import { TapcartClient } from "../shopify";
import { CoverageNotShowingReason } from "../storefront-debugger/coverage-debug-init";
import { getStorefrontAccessToken } from "../utils";
import { BigcommerceStorefrontClient } from "./bigcommerce-storefront";
import { CommercecloudStorefrontClient } from "./commercecloud-storefront";
import { createCommercecloudStorefrontClient } from "./commercecloud-storefront-factory";
import { LightopiaStorefrontClient } from "./lightopia";
import { MivaStorefrontClient } from "./miva-storefront";

export interface CreateExtensionShopifyClientOptions {
  autocheck: WidgetAutocheck;
  onTapcart: boolean;
}

export async function createExtensionShopifyClient(
  autocheck: WidgetAutocheck,
  onTapcart: boolean,
): Promise<ExtensionShopifyClient> {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (globalThis.redoStorefront?.type) {
    case "bigcommerce": {
      return new BigcommerceStorefrontClient({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        storeUrl: globalThis.redoStorefront.storeUrl!,
        auth: getStorefrontAccessToken(autocheck),
        widgetSlug: autocheck.widgetSlug,
      });
    }
    case "commercecloud": {
      const config = autocheck.storefrontClient?.sfcc;
      if (!CommercecloudStorefrontClient.resolveSiteId(config)) {
        globalThis.redoStorefront.coverageNotShowingReason =
          CoverageNotShowingReason.SFCC_SITE_ID_MISSING;
        throw new Error(
          "SFCC site id is not set by the cartridge or the Commerce Cloud integration",
        );
      }

      const client = createCommercecloudStorefrontClient({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        storeUrl: globalThis.redoStorefront.storeUrl!,
        config,
      });
      if (
        doesAttachmentStrategyUseCustomFee[autocheck.attachmentStrategy] &&
        client.usesCityBeachAdjustments
      ) {
        // City Beach runs its own adjustment controllers, so there is no cartridge marker to gate on.
        if (!(await client.coverageFee.isAvailable())) {
          globalThis.redoStorefront.coverageNotShowingReason =
            CoverageNotShowingReason.CITY_BEACH_ADJUSTMENT_CONTROLLER_MISSING;
          throw new Error(
            "City Beach Redo-GetAdjustments controller is not available",
          );
        }
        return client;
      }
      if (doesAttachmentStrategyUseCustomFee[autocheck.attachmentStrategy]) {
        /** Sync init-time gate on the cartridge's on-page marker; the probe below stays the authoritative deeper check. */
        const marker = globalThis.redoStorefront.sfcc;
        if (!marker) {
          globalThis.redoStorefront.coverageNotShowingReason =
            CoverageNotShowingReason.SFCC_CARTRIDGE_NOT_LOADED;
          throw new Error(
            "window.redoStorefront.sfcc is missing: the storefront-embed cartridge template is not rendering on this page",
          );
        }
        if (marker.settings?.coverageFeeEnabled === false) {
          globalThis.redoStorefront.coverageNotShowingReason =
            CoverageNotShowingReason.SFCC_CARTRIDGE_FEE_DISABLED;
          throw new Error(
            "window.redoStorefront.sfcc.settings.coverageFeeEnabled is false: the cartridge has the coverage fee disabled for this site",
          );
        }
        const available = await client.coverageFee.isAvailable();
        if (!available) {
          globalThis.redoStorefront.coverageNotShowingReason =
            CoverageNotShowingReason.SFCC_FEE_CONTROLLER_MISSING;
          throw new Error(
            "SFCC custom fee controller is not available on this cartridge",
          );
        }
      }
      return client;
    }
    case "custom-lightopia": {
      return new LightopiaStorefrontClient();
    }
    case "miva": {
      const config = globalThis.redoStorefront.miva;
      if (!config?.storeCode) {
        globalThis.redoStorefront.coverageNotShowingReason =
          CoverageNotShowingReason.MIVA_STORE_CODE_MISSING;
        throw new Error("Miva store code is not set by the embed script");
      }
      return new MivaStorefrontClient({
        ...config,
        coverageProductIds: autocheck.coverageProductIds,
      });
    }
    default:
      if (onTapcart) {
        return new TapcartClient(
          getStorefrontAccessToken(autocheck),
          (globalThis as any).STORE_URL,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          globalThis.Tapcart!,
        );
      } else {
        return ShopifyAjaxClient.fromEnv();
      }
  }
}
